// src/pages/WorkerDocuments.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_WRK = process.env.REACT_APP_API_WRK;

const WorkerDocuments = () => {
  const [worker, setWorker] = useState(null);
  const navigate = useNavigate();
  const workerId = localStorage.getItem("workerId");

  useEffect(() => {
    if (!workerId) {
      navigate("/worker-login");
      return;
    }

    axios
      .get(`${API_WRK}/profile/${workerId}`)
      .then((res) => setWorker(res.data))
      .catch((err) => console.error("❌ Failed to fetch worker:", err));
  }, [workerId, navigate]);

  const statusBadge = (s) => {
    if (s === "approved")
      return (
        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold shadow-sm">
          Completed ✓
        </span>
      );
    if (s === "rejected")
      return (
        <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-semibold shadow-sm">
          Rejected ✕
        </span>
      );
    return (
      <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-semibold shadow-sm animate-pulse">
        Pending…
      </span>
    );
  };

  if (!worker)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading…
      </div>
    );

  const certStatus = (() => {
    if (!worker.services || worker.services.length === 0) return "pending";
    const allApproved = worker.services.every((s) => s.certStatus === "approved");
    const anyRejected = worker.services.some((s) => s.certStatus === "rejected");
    if (allApproved) return "approved";
    if (anyRejected) return "rejected";
    return "pending";
  })();

  const Card = ({ title, desc, status, onClick }) => (
    <div
      onClick={onClick}
      className="group bg-white/80 backdrop-blur-xl border border-gray-200 hover:border-indigo-300 
                 rounded-2xl shadow-md hover:shadow-xl transition-all p-5 cursor-pointer flex items-center justify-between"
    >
      <div>
        <p className="font-semibold text-gray-900 text-[15px]">{title}</p>
        <p className="text-sm text-gray-500 mt-1">{desc}</p>
      </div>
      <div className="ml-4">{statusBadge(status)}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* HEADER */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent drop-shadow">
            📂 Your Documents
          </h1>
          <p className="text-gray-500 mt-2">
            Manage verification, IDs, and certifications
          </p>
        </div>

        <div className="space-y-5">
          {/* PROFILE PHOTO */}
          <Card
            title="Profile Photo"
            desc="Used for identity & verification"
            status={worker.profilePhotoStatus}
            onClick={() => navigate("/upload-profile-photo")}
          />

          {/* BACKGROUND CHECK */}
          <Card
            title="Background Check"
            desc="Upload your government-issued ID"
            status={worker.backgroundCheckStatus}
            onClick={() =>
              navigate(
                worker.backgroundCheckStatus === "rejected"
                  ? "/reupload-id"
                  : "/background-check"
              )
            }
          />

          {/* WORK ELIGIBILITY */}
          {worker.isInternational && (
            <Card
              title="Proof of Work Eligibility"
              desc="Permit required for non-citizens"
              status={worker.permitStatus}
              onClick={() =>
                navigate(
                  worker.permitStatus === "rejected"
                    ? "/reupload-id"
                    : "/background-check"
                )
              }
            />
          )}

          {/* WORK CERTIFICATIONS */}
          <Card
            title="Work Certifications"
            desc="For selected job categories"
            status={certStatus}
            onClick={() => navigate("/work-certifications")}
          />

          {/* RIDE DOCUMENTS */}
          {worker.driverProfile && (
            <Card
              title="Ride Documents"
              desc="Car details • License • Insurance"
              status={worker.driverProfile.status}
              onClick={() => navigate("/ride-documents")}
            />
          )}

          {/* TERMS */}
          <Card
            title="Terms and Conditions"
            desc="Accept before starting work"
            status={worker.termsAccepted ? "approved" : "pending"}
            onClick={() => {
              if (!worker.termsAccepted) navigate("/terms");
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkerDocuments;
